// AppShell — T-021 + T-053. The authenticated app layout.

import { useEffect, useMemo, useState, type JSX } from "react";
import { NavLink, Route, Routes, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { chainsByCategory } from "../../data/convictConditioning";
import { eliteChainsByCategory } from "../../data/eliteSkills";
import {
  aggregateProgressWithStandards,
  computeRecoveryScore,
  computeStreak,
  TIER_SETS,
} from "../../utils/progressionEngine";
import { localIsoDate } from "../../utils/dates";
import type {
  Exercise,
  ExerciseProgress,
  ProgressionStandard,
  WorkoutLog,
} from "../../types/calisthenics";
import { LiveWorkoutRunner } from "../workout/LiveWorkoutRunner";
import { SkillTreeViewer } from "../progression/SkillTreeViewer";
import { SettingsRoute } from "./SettingsRoute";
import { ActivityHeatmap } from "../analytics/ActivityHeatmap";
import { RecoveryIndicator } from "../analytics/RecoveryIndicator";
import { StreakCard } from "../analytics/StreakCard";
import { getDb, softDeleteWorkout } from "../../services/db";
import { loadWorkoutHistory } from "../../services/history";
import { useAsyncData } from "../../hooks/useAsyncData";
import { useActiveUserId } from "../../hooks/useActiveUserId";
import { EmptyState } from "../common/EmptyState";
import { ErrorState } from "../common/ErrorState";
import { useLoadingTimeout } from "../../hooks/useLoadingTimeout";

const ALL_CHAINS: Exercise[][] = [...chainsByCategory, ...eliteChainsByCategory];
const ALL_EXERCISES: Exercise[] = ALL_CHAINS.flat();

export function AppShell(): JSX.Element {
  const { t } = useI18n();
  const { signOut } = useAuth();
  return (
    <div
      data-testid="app-shell"
      className="min-h-screen bg-[var(--color-void)] text-[var(--color-ink)]"
    >
      {/* GlobalStatusBar is mounted in src/App.tsx (AdaptiveStatusBar) above
          the router for every route; do NOT mount it here too — it would
          double-mount on every /app/* screen. */}
      <header className="flex items-center justify-between border-b border-[var(--color-line)] px-4 py-3">
        <NavLink
          to="/app/workout"
          className="font-display text-2xl uppercase tracking-wider text-[var(--color-lime)]"
        >
          {t("brand.name")}
        </NavLink>
        <button
          type="button"
          onClick={() => {
            void signOut();
          }}
          data-testid="shell-signout"
          className="border border-[var(--color-line-strong)] px-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
          style={{ minHeight: "44px", borderRadius: "var(--radius-sharp)" }}
        >
          {t("auth.signOut")}
        </button>
      </header>
      <nav
        role="tablist"
        aria-label="primary"
        className="flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border-b border-[var(--color-line)]"
      >
        <ShellTab to="/app/workout" label={t("nav.workout")} />
        <ShellTab to="/app/tree" label={t("nav.tree")} />
        <ShellTab to="/app/progress" label={t("nav.progress")} />
        <ShellTab to="/app/settings" label={t("nav.settings")} />
        <ShellTab to="/app/coach" label={t("nav.coach")} />
      </nav>
      <main>
        <Routes>
          <Route path="workout" element={<WorkoutRoute />} />
          <Route path="tree" element={<TreeRoute />} />
          <Route path="progress" element={<ProgressRoute />} />
          <Route path="settings" element={<SettingsRoute />} />
          <Route path="coach" element={<CoachRoute />} />
        </Routes>
      </main>
    </div>
  );
}

function ShellTab({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <NavLink
      to={to}
      role="tab"
      data-testid={`tab-${to.split("/").pop()}`}
      className={({ isActive }) =>
        "px-4 py-2 font-subhead text-sm uppercase tracking-[0.06em] border-b-2 " +
        (isActive
          ? "border-[var(--color-lime)] text-[var(--color-ink-hi)]"
          : "border-transparent text-[var(--color-ink-mute)] hover:text-[var(--color-ink)]")
      }
    >
      {label}
    </NavLink>
  );
}

function WorkoutRoute(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const slug = params.get("exercise");
  const resume = params.get("workout");
  // T-090 R-25 H3: the source of truth for the exercise on Resume is
  // the slug ProgressRoute appends. Without it the runner renders
  // `pushup-1` and resumed sets land on the wrong exercise. The
  // search-param fallback chain stays as a defensive default.
  // `setCount` uses `TIER_SETS.mastery` instead of the hard-coded `3`
  // so a future tier addition (e.g. mastery becomes 5 sets)
  // changes one number, not every call site (commit message said
  // this was already done; it wasn't).
  const exercise = useMemo<Exercise>(
    () => ALL_EXERCISES.find((e) => e.slug === slug) ?? ALL_EXERCISES[0]!,
    [slug],
  );
  return (
    <LiveWorkoutRunner
      exercise={exercise}
      setCount={TIER_SETS.mastery}
      resumeWorkoutId={resume ?? undefined}
      onResolved={() => navigate("/app/tree")}
    />
  );
}

/** T-090 R-25 H3: map an exercise id from `UnfinishedWorkout.exerciseIds`
 *  back to the slug the runner uses. The route builds the URL from
 *  this so the runner renders the right exercise on the first
 *  paint (no async race with StrictMode). */
function exerciseSlugFor(exerciseIds: string[]): string | undefined {
  if (exerciseIds.length === 0) return undefined;
  const ex = ALL_EXERCISES.find((e) => e.id === exerciseIds[0]);
  return ex?.slug;
}

function TreeRoute(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("pushup");
  const chain = useMemo(
    () => ALL_CHAINS.find((c) => c[0]?.category === selected) ?? chainsByCategory[0]!,
    [selected],
  );
  const categoryNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of ALL_CHAINS) {
      const first = c[0];
      if (first) map[first.category] = first.category;
    }
    return map;
  }, []);

  // Same cold-load shape as ProgressRoute: subscribe to the active
  // database so the tree reloads once it opens instead of showing an
  // empty progress map until something else re-renders.
  const ownerId = useActiveUserId();
  const { data: progress, loading, error, retry } = useChainProgress(chain, ownerId);

  return (
    <section
      data-testid="tree-route"
      className="mx-auto w-full max-w-[640px] px-4 py-6"
    >
      <div className="mb-6 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("landing.big6.title")}
          </span>
          <label className="flex items-center gap-2">
            <span className="sr-only">Select chain</span>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="chain-selector"
              className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-2 py-1 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-hi)]"
              style={{ borderRadius: "var(--radius-sharp)" }}
            >
              {ALL_CHAINS.map((c) => {
                const cat = c[0]?.category ?? "";
                return (
                  <option key={cat} value={cat}>
                    {categoryNames[cat] ?? cat}
                  </option>
                );
              })}
            </select>
          </label>
        </div>

        {/* Big 6 Movement Grid */}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {ALL_CHAINS.slice(0, 6).map((c) => {
            const cat = c[0]?.category ?? "";
            const active = selected === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelected(cat)}
                className={
                  "flex flex-col items-center justify-center p-2 text-center transition-all " +
                  (active
                    ? "border-2 border-[var(--color-lime)] bg-[var(--color-lime)] font-bold text-[var(--color-void)] shadow-[0_0_12px_rgba(204,255,0,0.3)]"
                    : "border border-[var(--color-line-strong)] bg-[var(--color-slab)] text-[var(--color-ink-hi)] hover:border-[var(--color-lime)]/50 hover:bg-[var(--color-slab-2)] active:scale-95")
                }
                style={{ borderRadius: "var(--radius-card)", minHeight: "56px" }}
              >
                <span className="font-display text-xs uppercase tracking-[0.02em] leading-tight">
                  {categoryNames[cat] ?? cat}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!loading && !error && progress && Object.keys(progress).length === 0 && (
        <div className="mt-4">
          <EmptyState
            title={t("status.emptyTreeTitle")}
            body={t("status.emptyTreeBody")}
          />
        </div>
      )}

      {error && (
        <div className="mt-4">
          <ErrorState
            title={t("status.errorTitle")}
            body={t("status.errorBody")}
            retryLabel={t("status.errorRetry")}
            onRetry={retry}
          />
        </div>
      )}

      <SkillTreeViewer
        chain={chain}
        progress={progress ?? {}}
        onStart={(exercise) => navigate(`/app/workout?exercise=${exercise.slug}`)}
      />
    </section>
  );
}

function ProgressRoute(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [today, setToday] = useState(() => localIsoDate());

  useEffect(() => {
    const update = () => setToday(localIsoDate());
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  // The owner of the ACTIVE database, as state — not `user.id`. On a cold
  // load of this URL the user is known before the AuthProvider's effect
  // has opened the per-user database (child effects run first), and a
  // query in that gap throws "db: no active database" and rendered the
  // error boundary. Present since T-053; found by the human's production
  // walk on 2026-09-06. While no database is active we are loading, not
  // empty and not failed.
  const ownerId = useActiveUserId();
  const dbReady = ownerId !== null;
  const {
    data: history,
    loading,
    error,
    retry,
  } = useAsyncData(
    () => (ownerId ? loadWorkoutHistory(ownerId) : Promise.resolve(undefined)),
    5000,
    [ownerId],
  );
  const timedOut = useLoadingTimeout(!dbReady || loading, 5000);

  if (error || timedOut) {
    return (
      <section
        data-testid="progress-route"
        className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-6"
      >
        <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("nav.progress")}
        </h1>
        <ErrorState
          title={timedOut ? t("status.timeoutTitle") : t("status.errorTitle")}
          body={timedOut ? t("status.timeoutBody") : t("status.errorBody")}
          retryLabel={t("status.errorRetry")}
          onRetry={retry}
        />
      </section>
    );
  }

  if (!dbReady || loading || !history) {
    return (
      <section
        data-testid="progress-route"
        className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-6"
      >
        <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("nav.progress")}
        </h1>
        <p className="font-sans text-sm text-[var(--color-ink-mute)]" data-testid="progress-loading">
          {t("status.loadingTitle")}
        </p>
      </section>
    );
  }

  // ADR-0013 D3: streak/heatmap/recovery count both completed and
  // unfinished workouts (the person trained); the list rendering
  // below separates them. Tier derivation is independent.
  const allWorkouts: WorkoutLog[] = [
    ...history.completed.map((w) => ({
      date: w.date,
      totalReps: w.totalReps,
      totalSeconds: w.totalSeconds,
    })),
    ...history.unfinished.map((w) => ({
      date: w.date,
      totalReps: w.totalReps,
      totalSeconds: w.totalSeconds,
    })),
  ];

  if (allWorkouts.length === 0) {
    return (
      <section
        data-testid="progress-route"
        className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-6"
      >
        <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("nav.progress")}
        </h1>
        <div className="grid gap-4 sm:grid-cols-2">
          <RecoveryIndicator score={0} today={today} />
          <StreakCard days={0} />
        </div>
        <div
          className="relative overflow-hidden border border-[var(--color-line)] bg-[var(--color-slab)] p-6"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <ActivityHeatmap dates={[]} today={today} />
          <div className="mt-6 flex flex-col items-center gap-3 border-t border-[var(--color-line)] pt-6 text-center">
            <h2 className="font-display text-xl uppercase tracking-[0.02em] text-[var(--color-lime)]">
              {t("status.emptyProgressOnboardingTitle")}
            </h2>
            <p className="max-w-md font-sans text-sm text-[var(--color-ink-mute)]">
              {t("status.emptyProgressOnboardingBody")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/app/workout")}
              className="mt-2 inline-flex items-center justify-center border border-[var(--color-lime)] bg-[var(--color-lime)] px-6 py-3 font-subhead text-sm font-bold uppercase tracking-[0.06em] text-[var(--color-void)] hover:bg-[var(--color-lime)]/90 active:scale-95 shadow-[0_0_16px_rgba(204,255,0,0.25)]"
              style={{ borderRadius: "var(--radius-sharp)" }}
            >
              {t("status.emptyProgressStartCta")}
            </button>
          </div>
        </div>
        <EmptyState
          title={t("status.emptyProgressTitle")}
          body={t("status.emptyProgressBody")}
        />
      </section>
    );
  }

  const dates = allWorkouts.map((h) => h.date);
  const streak = computeStreak(dates, today);
  const recovery = computeRecoveryScore(allWorkouts, today);
  return (
    <section
      data-testid="progress-route"
      className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-6"
    >
      <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
        {t("nav.progress")}
      </h1>
      <div className="grid gap-4 sm:grid-cols-2">
        <RecoveryIndicator score={recovery} today={today} />
        <StreakCard days={streak} />
      </div>
      <ActivityHeatmap dates={dates} today={today} />
      {history.unfinished.length > 0 && (
        <section data-testid="unfinished-list" aria-label={t("history.unfinishedTitle")}>
          <h2 className="font-display text-xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
            {t("history.unfinishedTitle")}
          </h2>
          <ul className="mt-3 flex flex-col gap-2">
            {history.unfinished.map((w) => (
              <li
                key={w.id}
                data-testid="unfinished-row"
                className="flex flex-col items-stretch gap-3 border border-[var(--color-line)] bg-[var(--color-slab)] p-3 sm:flex-row sm:items-center sm:justify-between"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <span className="font-sans text-sm text-[var(--color-ink)]">
                  {w.date} · {t("history.rowSummary", { reps: w.totalReps, sets: w.setCount })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="history-resume"
                    onClick={() => {
                      const slug = exerciseSlugFor(w.exerciseIds);
                      const qs = slug
                        ? `?workout=${w.id}&exercise=${slug}`
                        : `?workout=${w.id}`;
                      navigate(`/app/workout${qs}`);
                    }}
                    className="border border-[var(--color-lime)] bg-[var(--color-lime)] px-3 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-void)]"
                    style={{ borderRadius: "var(--radius-sharp)" }}
                  >
                    {t("history.resume")}
                  </button>
                  <button
                    type="button"
                    data-testid="history-discard"
                    onClick={() => {
                      void softDeleteWorkout(w.id).catch(() => undefined);
                    }}
                    className="border border-[var(--color-line-strong)] px-3 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
                    style={{ borderRadius: "var(--radius-sharp)" }}
                  >
                    {t("history.discard")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function CoachRoute(): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  if (user?.role !== "coach") {
    return (
      <section
        data-testid="coach-route"
        className="mx-auto flex w-full max-w-[640px] flex-col gap-4 px-4 py-12"
      >
        <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("coach.title")}
        </h1>
        <p className="font-sans text-base text-[var(--color-ink-mute)]">
          {t("coach.athleteOnly")}
        </p>
      </section>
    );
  }
  return (
    <section
      data-testid="coach-route"
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4 px-4 py-12"
    >
      <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
        {t("coach.title")}
      </h1>
      <p className="font-sans text-base text-[var(--color-ink)]">
        {t("coach.placeholder")}
      </p>
      <p className="font-sans text-base text-[var(--color-ink)]">
        {t("coach.placeholderBody")}
      </p>
      <Link
        to="/app/workout"
        className="mt-2 inline-flex w-fit items-center justify-center px-5 py-2 font-subhead text-sm uppercase tracking-[0.06em]"
        style={{
          minHeight: "44px",
          borderRadius: "var(--radius-sharp)",
          backgroundColor: "var(--color-lime)",
          color: "var(--color-void)",
        }}
      >
        {t("coach.backToWorkout")}
      </Link>
    </section>
  );
}

/** Aggregate per-exercise progress from local rows for the skill tree. */
function useChainProgress(
  chain: readonly Exercise[],
  ownerId: string | null,
): {
  data: Record<string, ExerciseProgress> | undefined;
  loading: boolean;
  error: Error | null;
  retry: () => void;
} {
  return useAsyncData(async () => {
    if (!ownerId) return {};
    const db = getDb();
    // T-090 R-25 H1 + the ADR-0013 D5 fallback. We read two views and
    // merge them: the durable `user_progress` row (which carries
    // `current_status` — the live server's required select — so a
    // pulled row from another device still shows a tier) and the
    // local derivation over completed workouts (the fallback, which
    // computes `bestTier` for an exercise that has not been folded
    // yet, e.g. when a `user_progress` write failed or was never
    // pulled). The fallback alone closed the audit's second-half H1
    // finding: a 3×50 workout whose row failed or was never pulled
    // used to show *inProgress* and never unlock the next step.
    const [progressRows, completedWorkouts, logEntries] = await Promise.all([
      db.user_progress.where("owner").equals(ownerId).and((r) => !r.deletedAt).toArray(),
      db.workout_logs.where("owner").equals(ownerId).and((r) => !r.deletedAt && r.completed_at != null).toArray(),
      db.log_entries.where("owner").equals(ownerId).and((r) => !r.deletedAt).toArray(),
    ]);
    const next: Record<string, ExerciseProgress> = {};
    for (const r of progressRows) {
      next[r.exercise] = {
        exerciseId: r.exercise,
        bestTier: readStoredTier(r),
        bestReps: 0,
        bestHoldSeconds: 0,
        completedWorkouts: 0,
      };
    }
    // Fallback: derive the tier from completed workouts locally. The
    // union of standards comes from the chain; a row's maxima stay
    // local-only. `aggregateProgressWithStandards` ignores
    // unfinished and tombstoned workouts by construction (R-25 §3).
    const standards: Record<string, ProgressionStandard> = {};
    for (const ex of chain) standards[ex.id] = ex.standard;
    const agg = aggregateProgressWithStandards(
      completedWorkouts,
      logEntries,
      standards,
    );
    for (const [exId, computed] of Object.entries(agg)) {
      const cur = next[exId] ?? {
        exerciseId: exId,
        bestTier: "none" as ExerciseProgress["bestTier"],
        bestReps: 0,
        bestHoldSeconds: 0,
        completedWorkouts: 0,
      };
      // The stored row is the durable truth; if it's there we keep
      // its tier but accept the local derivation's maxima (the
      // server doesn't carry them; a pulled row has them at 0). The
      // local derivation never demotes a stored tier — only raises
      // it — so a 3×50 the user just did always moves the node.
      if (tierRank(computed.bestTier) > tierRank(cur.bestTier)) {
        cur.bestTier = computed.bestTier;
      }
      if (computed.bestReps > cur.bestReps) cur.bestReps = computed.bestReps;
      if (computed.bestHoldSeconds > cur.bestHoldSeconds) cur.bestHoldSeconds = computed.bestHoldSeconds;
      cur.completedWorkouts = Math.max(cur.completedWorkouts, computed.completedWorkouts);
      next[exId] = cur;
    }
    // Only return entries for exercises actually in this chain.
    const filtered: Record<string, ExerciseProgress> = {};
    for (const ex of chain) {
      if (next[ex.id]) filtered[ex.id] = next[ex.id]!;
    }
    return filtered;
  }, 5000, [chain, ownerId]);
}

/** T-090 R-25 H1: read the tier stored on a `user_progress` row.
 *  Pre-H1 rows carried `bestTier`; H1+ rows carry `current_status`.
 *  Read whichever is there and normalise to `ExerciseProgress.bestTier`. */
function readStoredTier(row: {
  current_status?: string;
  bestTier?: string;
}): ExerciseProgress["bestTier"] {
  const v = (row.current_status ?? row.bestTier ?? "locked") as string;
  if (v === "beginner" || v === "intermediate" || v === "mastery") return v;
  return "none";
}

/** Numeric tier rank used by useChainProgress's merge. Local copy
 *  (the runner's `tierRank` is module-private and we don't import
 *  across modules for a one-liner). */
function tierRank(t: ExerciseProgress["bestTier"]): number {
  switch (t) {
    case "mastery":
      return 3;
    case "intermediate":
      return 2;
    case "beginner":
      return 1;
    case "none":
    default:
      return 0;
  }
}
